/**
 * Servicio API para comunicación con el backend Node.js
 */

import { ProfileData } from '../types/profile.types';

// URL base del backend Node.js
// En desarrollo local, usar URL completa del backend. En producción (Vercel), usar ruta relativa
const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return '/api';
})();

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
  profileId?: number;
  profile?: any;
}

/**
 * Guardar o actualizar perfil
 */
export const saveProfile = async (profileData: ProfileData): Promise<ApiResponse> => {
  try {

    const response = await fetch(`${API_BASE_URL}/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Para mantener la sesión
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      // Intentar parsear el error como JSON para obtener el mensaje del servidor
      let errorMessage = `Error del servidor (${response.status})`;
      let errorDetails = null;
      let isTimeout = false;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) {
          errorMessage = errorJson.error;
        }
        if (errorJson.details) {
          errorDetails = errorJson.details;
        }
        if (errorJson.isTimeout) {
          isTimeout = true;
        }
      } catch (e) {
        // Si no es JSON, usar el texto del error
        if (errorText) {
          errorMessage = errorText.substring(0, 200);
        }
      }

      // Crear un error con información del status para poder detectarlo después
      const error: any = new Error(errorMessage);
      error.status = response.status;
      error.statusCode = response.status;
      error.statusText = response.statusText;
      error.details = errorDetails;
      error.isTimeout = isTimeout;
      throw error;
    }

    const data: ApiResponse = await response.json();
    return data;
  } catch (error: any) {
    // Preservar el status del error si existe
    const errorResponse: ApiResponse = {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido al guardar el perfil',
    };
    // Agregar status al objeto de respuesta para que pueda ser detectado
    if (error?.status || error?.statusCode) {
      (errorResponse as any).status = error.status || error.statusCode;
    }
    // Agregar isTimeout si existe
    if (error?.isTimeout) {
      (errorResponse as any).isTimeout = true;
    }
    return errorResponse;
  }
};

/**
 * Guardar solo una sección específica del perfil (para payloads grandes)
 */
export const saveProfileSection = async (section: string, data: any): Promise<ApiResponse> => {
  try {

    const response = await fetch(`${API_BASE_URL}/save-section`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ section, data }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Error del servidor (${response.status})`;
      let isTimeout = false;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) {
          errorMessage = errorJson.error;
        }
        if (errorJson.isTimeout) {
          isTimeout = true;
        }
      } catch (e) {
        if (errorText) {
          errorMessage = errorText.substring(0, 200);
        }
      }

      const error: any = new Error(errorMessage);
      error.status = response.status;
      error.statusCode = response.status;
      error.isTimeout = isTimeout;
      throw error;
    }

    const result: ApiResponse = await response.json();
    return result;
  } catch (error: any) {
    // Preservar el status del error si existe
    const errorResponse: ApiResponse = {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido al guardar la sección',
    };
    // Agregar status al objeto de respuesta para que pueda ser detectado
    if (error?.status || error?.statusCode) {
      (errorResponse as any).status = error.status || error.statusCode;
    }
    // Agregar isTimeout si existe
    if (error?.isTimeout) {
      (errorResponse as any).isTimeout = true;
    }
    return errorResponse;
  }
};

/**
 * Actualizar solo el campo de autorización para uso de información
 */
export const updateDraftStatus = async (isDraft: boolean): Promise<ApiResponse> => {
  try {

    const response = await fetch(`${API_BASE_URL}/update-draft-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ isDraft: isDraft }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `Error del servidor (${response.status})`;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) {
          errorMessage = errorJson.error;
        }
      } catch (e) {
        if (errorText) {
          errorMessage = errorText.substring(0, 200);
        }
      }

      // Si es un error 404, verificar si es la ruta de autorización
      if (response.status === 404) {
        errorMessage = 'Error de conexión con el servidor. Por favor, verifique que el servidor esté corriendo.';
      }

      const error: any = new Error(errorMessage);
      error.status = response.status;
      error.statusCode = response.status;
      throw error;
    }

    const data: ApiResponse = await response.json();
    return data;
  } catch (error: any) {
    // Preservar el status del error si existe
    const errorResponse: ApiResponse = {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido al actualizar el estado del perfil',
    };
    // Agregar status al objeto de respuesta para que pueda ser detectado
    if (error?.status || error?.statusCode) {
      (errorResponse as any).status = error.status || error.statusCode;
    }
    return errorResponse;
  }
};

/**
 * Obtener perfil del usuario autenticado
 */
export const getProfileByEmail = async (email?: string): Promise<ApiResponse> => {
  try {

    const response = await fetch(`${API_BASE_URL}/get`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Para mantener la sesión
    });

    if (!response.ok) {
      const errorText = await response.text();
      // Intentar parsear el error como JSON para obtener el mensaje del servidor
      let errorMessage = `Error del servidor (${response.status})`;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) {
          errorMessage = errorJson.error;
        }
      } catch (e) {
        // Si no es JSON, usar el texto del error
        if (errorText) {
          errorMessage = errorText.substring(0, 200);
        }
      }

      throw new Error(errorMessage);
    }

    const data: ApiResponse = await response.json();
    return data;
  } catch (error: any) {
    // Preservar el status del error si existe
    const errorResponse: ApiResponse = {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido al obtener el perfil',
    };
    // Agregar status al objeto de respuesta para que pueda ser detectado
    if (error?.status || error?.statusCode) {
      (errorResponse as any).status = error.status || error.statusCode;
    }
    return errorResponse;
  }
};

/**
 * Inicializar tabla en base de datos (solo desarrollo)
 */
export const initializeDatabase = async (): Promise<ApiResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}/init`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: ApiResponse = await response.json();
    return data;
  } catch (error: any) {
    // Preservar el status del error si existe
    const errorResponse: ApiResponse = {
      success: false,
      error: error instanceof Error ? error.message : 'Error desconocido al inicializar la base de datos',
    };
    // Agregar status al objeto de respuesta para que pueda ser detectado
    if (error?.status || error?.statusCode) {
      (errorResponse as any).status = error.status || error.statusCode;
    }
    return errorResponse;
  }
};
